<script setup lang="ts">
import type { UseAtcWatchlist } from '../../composables/useAtcWatchlist'
import { ATC_MAX_CONFIGS } from '../../models/watchlist.types'

import AtcAdvertiserSearch from './AtcAdvertiserSearch.vue'
import AtcConfigCard from './AtcConfigCard.vue'

import { useI18n } from '@/shared/i18n'
import { Alert, Badge, Button, Icon } from '@/shared/ui'

defineProps<{ watchlist: UseAtcWatchlist }>()

const { t } = useI18n()
</script>

<template>
  <div class="atc-tab grid gap-md font-sans">
    <div class="atc-tab__intro flex items-start justify-between gap-md">
      <div>
        <h4>{{ t('workspaceDefinitions.watchlist.atc.title') }}</h4>
        <p>{{ t('workspaceDefinitions.watchlist.atc.description') }}</p>
      </div>
      <Badge :tone="watchlist.atCap.value ? 'warning' : 'info'">
        {{
          t('workspaceDefinitions.watchlist.atc.cap', {
            used: watchlist.drafts.value.length,
            max: ATC_MAX_CONFIGS,
          })
        }}
      </Badge>
    </div>

    <details
      class="atc-tab__help py-sm px-md border border-border rounded-md bg-surface-sunken text-[0.75rem]"
    >
      <summary>
        <Icon name="circle-question" :size="15" />
        {{ t('workspaceDefinitions.watchlist.atc.help.label') }}
      </summary>
      <ol>
        <li>{{ t('workspaceDefinitions.watchlist.atc.help.step1') }}</li>
        <li>{{ t('workspaceDefinitions.watchlist.atc.help.step2') }}</li>
        <li>{{ t('workspaceDefinitions.watchlist.atc.help.step3') }}</li>
        <li>{{ t('workspaceDefinitions.watchlist.atc.help.step4') }}</li>
      </ol>
      <a href="https://adstransparency.google.com/" target="_blank" rel="noopener noreferrer">
        {{ t('workspaceDefinitions.watchlist.atc.help.open') }}
        <Icon name="external-link" :size="13" />
      </a>
    </details>

    <AtcAdvertiserSearch
      :model-value="watchlist.query.value"
      :suggestions="watchlist.suggestions.value"
      :loading="watchlist.suggestionsLoading.value"
      :disabled="watchlist.atCap.value"
      :error-key="watchlist.addErrorKey.value"
      @update:model-value="watchlist.setQuery"
      @add="watchlist.addFromSearch"
    />

    <div class="atc-tab__section-heading flex items-start justify-between gap-md">
      <strong>
        {{
          t('workspaceDefinitions.watchlist.atc.tracked', {
            used: watchlist.drafts.value.length,
            max: ATC_MAX_CONFIGS,
          })
        }}
      </strong>
    </div>

    <Alert v-if="watchlist.saveErrorKey.value" tone="danger">
      {{ t(watchlist.saveErrorKey.value) }}
    </Alert>
    <Alert v-if="watchlist.validation.value.capExceeded" tone="warning">
      {{ t('workspaceDefinitions.watchlist.atc.error.cap') }}
    </Alert>

    <div v-if="watchlist.drafts.value.length" class="atc-tab__cards grid gap-sm">
      <AtcConfigCard
        v-for="(draft, index) in watchlist.drafts.value"
        :key="draft.uid"
        :draft="draft"
        :index="index"
        :expanded="watchlist.expandedUid.value === draft.uid"
        :errors="watchlist.validation.value.errors[draft.uid] ?? {}"
        :submitted="watchlist.hasSubmitted.value"
        @toggle="watchlist.toggleDraft(draft.uid)"
        @remove="watchlist.removeDraft(draft.uid)"
        @patch="watchlist.patchDraft(draft.uid, $event)"
        @touch="watchlist.touchField(draft.uid, $event)"
      />
    </div>
    <div
      v-else
      class="atc-tab__empty text-muted text-[0.8125rem] grid place-items-center gap-sm p-xl border border-dashed border-border rounded-md"
    >
      <p>{{ t('workspaceDefinitions.watchlist.atc.empty') }}</p>
      <Button variant="outline" size="sm" @click="watchlist.resetDrafts">
        {{ t('workspaceDefinitions.watchlist.atc.action.add') }}
      </Button>
    </div>
  </div>
</template>

<style scoped>
.atc-tab .atc-tab__intro h4 {
  margin: 0;
}
.atc-tab .atc-tab__intro p {
  margin: 0;
  color: var(--ui-color-muted);
  font-size: 0.8125rem;
}
.atc-tab .atc-tab__empty p {
  margin: 0;
}
.atc-tab .atc-tab__help summary {
  display: inline-flex;
  align-items: center;
  gap: var(--ui-space-2xs);
  color: var(--ui-color-primary);
  cursor: pointer;
}
.atc-tab .atc-tab__help a {
  display: inline-flex;
  align-items: center;
  gap: var(--ui-space-2xs);
  color: var(--ui-color-primary);
  cursor: pointer;
}
.atc-tab .atc-tab__help ol {
  margin-block: var(--ui-space-xs);
  padding-inline-start: var(--ui-space-lg);
}
</style>
